import { createSelector } from '@reduxjs/toolkit';

import type User from '@/data/user/model';
import { getAuthUser } from '@/shared/auth/selectors';
import type { WiwState } from '@/store';
import type { WorkchatAuthState } from '@/workchat/v2/store/auth/authReducer';
import { getConversationEntityMap } from '@/workchat/v2/store/conversations/conversationsSelectors';
import type { WorkchatState } from '@/workchat/v2/store/reducer';

export enum WorkChatTokenSource {
  Twilio = 'twilio_session',
  Stream = 'stream_session',
}

export const getWorkchatState = (state: WiwState) => state.workchatv2;

export const getWorkchatAuthState = createSelector(
  [getWorkchatState],
  (workchatState: WorkchatState) => workchatState.auth,
);

export const getWorkchatInitError = createSelector([getWorkchatState], (state: WorkchatState) => state.initError);

export const getWorkchatConnected = createSelector([getWorkchatState], (state: WorkchatState) => state.connected);
export const getWorkchatShow = createSelector([getWorkchatState], (workchatState: WorkchatState) => workchatState.show);
export const getWorkchatError = createSelector(
  [getWorkchatState],
  (workchatState: WorkchatState) => workchatState.error,
);

export const getWorkchatFlags = createSelector(
  [getWorkchatState],
  (workchatState: WorkchatState) => workchatState.flags,
);

export const getMessageReaders = createSelector(
  [getWorkchatState],
  (workchatState: WorkchatState) => workchatState.messageReaders,
);

export const getWorkchatView = createSelector([getWorkchatState], (workchatState: WorkchatState) =>
  workchatState.views.first(''),
);

export const getWorkchatSelectedConversationId = createSelector(
  [getWorkchatState],
  (workchatState: WorkchatState) => workchatState.conversation,
);

export const getWorkchatSelectedConversation = createSelector(
  [getConversationEntityMap, getWorkchatSelectedConversationId],
  (conversations, conversationId) => conversations.get(conversationId),
);

const tokenSelector = ({
  workchatAuth,
  user,
  source,
}: {
  workchatAuth: WorkchatAuthState;
  user: User;
  source: WorkChatTokenSource;
}) => {
  if (workchatAuth.token) {
    return workchatAuth.token;
  }

  let session: string | null;
  let sessionObj: { token: string; user_id: number };
  let token: string | null;

  try {
    session = localStorage.getItem(source);
    sessionObj = session && JSON.parse(session);
    token = sessionObj && sessionObj.user_id === user.id ? sessionObj.token : null;

    if (token) {
      return token;
    }
  } catch (_error) {
    console.warn(`Pre-existing Workchat token unavailable, source=${source}; Re-authenticating.`);
  }

  return null;
};

export const getWorkchatAuthToken = createSelector([getWorkchatAuthState, getAuthUser], (workchatAuth, user) =>
  tokenSelector({ workchatAuth, user, source: WorkChatTokenSource.Twilio }),
);

export const getWorkchatAuthTokenStream = createSelector([getWorkchatAuthState, getAuthUser], (workchatAuth, user) =>
  tokenSelector({ workchatAuth, user, source: WorkChatTokenSource.Stream }),
);

export const getWorkchatPendingConversation = createSelector(
  [getWorkchatState],
  workchatState => workchatState.pendingConversation,
);
